import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";

const T = {
  en: {
    signinTitle: "Sign in to Rang1",
    signupTitle: "Create your free account",
    signinSub: "Access your site audits and saved reports.",
    signupSub: "A free account unlocks the sitewide audit and local rank checker, and keeps your report history.",
    email: "Email",
    password: "Password",
    passwordNew: "At least 8 characters",
    passwordCurrent: "Your password",
    wait: "Please wait...",
    signIn: "Sign in",
    createAccount: "Create account",
    noAccount: "No account yet?",
    createFree: "Create one free",
    haveAccount: "Already have an account?",
    signInLink: "Sign in",
    toastIn: "Signed in",
    toastUp: "Account created - welcome!",
    freeNote: "The generator and checker tools stay free without an account.",
  },
  fr: {
    signinTitle: "Connexion à Rang1",
    signupTitle: "Créez votre compte gratuit",
    signinSub: "Accédez à vos audits de site et rapports sauvegardés.",
    signupSub: "Un compte gratuit débloque l'audit complet et le rang local, et conserve l'historique de vos rapports.",
    email: "Courriel",
    password: "Mot de passe",
    passwordNew: "Au moins 8 caractères",
    passwordCurrent: "Votre mot de passe",
    wait: "Un instant...",
    signIn: "Se connecter",
    createAccount: "Créer le compte",
    noAccount: "Pas encore de compte?",
    createFree: "Créez-en un gratuitement",
    haveAccount: "Déjà un compte?",
    signInLink: "Connectez-vous",
    toastIn: "Connecté",
    toastUp: "Compte créé - bienvenue!",
    freeNote: "Les générateurs et vérificateurs restent gratuits sans compte.",
  },
};

export default function Auth() {
  const [mode, setMode] = useState("signin"); // signin | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { saveSession } = useAuth();
  const { lang } = useLang();
  const t = T[lang];
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/site-audit";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const endpoint = mode === "signin" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(endpoint, { email: email.trim(), password });
      saveSession(data);
      toast.success(mode === "signin" ? t.toastIn : t.toastUp);
      navigate(next, { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-14 sm:py-20">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading">
            {mode === "signin" ? t.signinTitle : t.signupTitle}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {mode === "signin" ? t.signinSub : t.signupSub}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="auth-email">{t.email}</Label>
              <Input
                id="auth-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
                required
                data-testid="auth-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="auth-password">{t.password}</Label>
              <Input
                id="auth-password"
                type="password"
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "signup" ? t.passwordNew : t.passwordCurrent}
                minLength={8}
                required
                data-testid="auth-password-input"
              />
            </div>

            {error && (
              <Alert variant="destructive" data-testid="auth-error">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full h-10" disabled={loading} data-testid="auth-submit-button">
              {mode === "signin" ? <LogIn className="h-4 w-4 mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              {loading ? t.wait : mode === "signin" ? t.signIn : t.createAccount}
            </Button>
          </form>

          <p className="mt-4 text-sm text-muted-foreground text-center">
            {mode === "signin" ? (
              <>
                {t.noAccount}{" "}
                <button type="button" className="text-primary underline underline-offset-2" onClick={() => { setMode("signup"); setError(null); }} data-testid="auth-switch-signup">
                  {t.createFree}
                </button>
              </>
            ) : (
              <>
                {t.haveAccount}{" "}
                <button type="button" className="text-primary underline underline-offset-2" onClick={() => { setMode("signin"); setError(null); }} data-testid="auth-switch-signin">
                  {t.signInLink}
                </button>
              </>
            )}
          </p>
        </CardContent>
      </Card>
      <p className="mt-4 text-xs text-muted-foreground text-center">
        <Link to="/meta-generator" className="underline underline-offset-2">{t.freeNote}</Link>
      </p>
    </div>
  );
}
